import { rankMentions, searchMentions } from '../notes/noteMentions';
import type { MentionItem, MentionSources } from '../notes/noteMentions';
import type { SupabaseClient } from '../../lib/supabase';

/**
 * `@` in a comment means two different things.
 *
 * In a discussion thread it almost always means a PERSON — "@Dana can you
 * check this" — and that mention has a consequence: a row in comment_mentions,
 * which fires comment_mentions_notify() and puts the comment in that person's
 * Inbox. In a note, `@` means a RECORD: a project, a task, an order.
 *
 * The composer needs both, and this is where the two scopes meet. They stay
 * separable all the way through, because they end up as different things:
 *
 *   person  →  <mark class="comment-mention" data-id="…">   → a notification
 *   record  →  <span data-type="mention" data-href="/…">    → a link
 *
 * A single "mention" concept covering both would eventually insert a
 * comment_mentions row whose mentioned_user_id is a project id. The guard
 * trigger would refuse it — mention_must_be_active_tap_user — so nothing
 * unsafe happens, but the person would be told their comment failed for no
 * reason they could act on.
 */

export type CommentMentionScope = 'person' | 'record';

export interface CommentMentionRow {
  id: string;
  label: string;
  hint: string;
  group: string;
  scope: CommentMentionScope;
  /** Records only. A person mention links nowhere. */
  href: string;
}

export interface MentionPerson {
  id: string;
  label: string;
  /** Available on Messages so /killua can match the already loaded chat list. */
  email?: string;
  /** The current person may start a DM with this active TAP member. */
  mayStartDm?: boolean;
}

/** How many people to offer. The record search caps itself at five per kind. */
const PEOPLE_LIMIT = 6;

/**
 * The people worth offering for what has been typed.
 *
 * Filtered in the browser, not queried. The full list of active TAP people is
 * already loaded for the thread (useTapPeople), it is small, and a round trip
 * per keystroke to re-fetch rows we are holding would make the popover feel
 * slower than typing.
 */
export function matchPeople(people: MentionPerson[], query: string): CommentMentionRow[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') {
    return [];
  }
  const matches = people.filter((person) => person.label.toLowerCase().includes(needle));
  // Reuses the record ranker so both halves of the list agree about what a
  // good match is: exact, then prefix, then word-start, then anywhere.
  const ranked = rankMentions(
    matches.map((person) => ({
      id: person.id,
      kind: 'project' as const,
      label: person.label,
      href: '',
      hint: '',
    })),
    needle,
  );
  return ranked.slice(0, PEOPLE_LIMIT).map((item) => ({
    id: item.id,
    label: item.label,
    hint: 'Person',
    group: 'People',
    scope: 'person' as const,
    href: '',
  }));
}

function recordRows(items: MentionItem[]): CommentMentionRow[] {
  return items.map((item) => ({
    id: item.id,
    label: item.label,
    hint: item.hint,
    group: 'Records',
    scope: 'record' as const,
    href: item.href,
  }));
}

/**
 * Both scopes, people first.
 *
 * PEOPLE ALWAYS COME FIRST, and not because they scored better. `@` in a
 * thread means a colleague far more often than it means a record, and the
 * first row is the one Enter picks. Interleaving the two by relevance would
 * mean that typing a name that also appears in a project title sends the
 * notification nowhere.
 */
export async function searchCommentMentions(
  client: SupabaseClient,
  query: string,
  sources: MentionSources,
  people: MentionPerson[],
): Promise<CommentMentionRow[]> {
  const trimmed = query.trim();
  if (trimmed === '') {
    return [];
  }
  const records = await searchMentions(client, trimmed, sources);
  return [...matchPeople(people, trimmed), ...recordRows(records)];
}

/** What the Mention node stores for a picked row. */
export function commentMentionAttrs(row: CommentMentionRow): Record<string, unknown> {
  return {
    id: row.id,
    label: row.label,
    // The kind is what decides the tag on the way out — a <mark> that carries
    // a notification, or a <span> that carries a link. Storing it on the node
    // rather than inferring it from whether href is empty means a record whose
    // URL is missing still renders as a record.
    scope: row.scope,
    href: row.scope === 'record' ? row.href : '',
  };
}
